import React, { useEffect, useMemo, useState } from "react";

import { BrokenLinkIcon } from "@/assets/icons";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from "@/components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

import { twMerge } from "tailwind-merge";

import { FeaturePhotoItem } from "./types";

interface FeatureInfoPhotosProps {
  formattedKey?: string;
  photos: FeaturePhotoItem[];
}

const FeatureInfoPhotos = ({
  formattedKey,
  photos
}: FeatureInfoPhotosProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const thumbnailItems = useMemo(() => photos, [photos]);

  useEffect(() => {
    const api = carouselApi;
    if (!api) return;

    const handleSelect = () => {
      setSelectedIndex(api.selectedScrollSnap());
    };

    handleSelect();

    api.on("select", handleSelect);
    api.on("reInit", handleSelect);

    return () => {
      api.off?.("select", handleSelect);
      api.off?.("reInit", handleSelect);
    };
  }, [carouselApi]);

  useEffect(() => {
    if (!carouselApi) return;
    const maxLength = thumbnailItems.length;
    if (selectedIndex < 0 || selectedIndex >= maxLength) return;
    if (carouselApi.selectedScrollSnap() === selectedIndex) return;
    carouselApi.scrollTo(selectedIndex, true);
  }, [selectedIndex, carouselApi, thumbnailItems.length]);

  useEffect(() => {
    if (selectedIndex >= thumbnailItems.length) {
      setSelectedIndex(0);
    }
  }, [thumbnailItems.length, selectedIndex]);

  const hasMultiplePhotos = thumbnailItems.length > 1;

  const handleThumbnailClick = (index: number) => {
    setSelectedIndex(index);
  };

  const handleOpenViewer = () => {
    setIsViewerOpen(true);
  };

  const handleViewerOpenChange = (isOpen: boolean) => {
    setIsViewerOpen(isOpen);
  };

  const selectedPhoto = thumbnailItems[selectedIndex];
  const label = formattedKey ?? "Photos";

  const getFirstValidUrl = (candidates: Array<string | undefined>): string => {
    for (const candidate of candidates) {
      if (!candidate) continue;
      const trimmed = candidate.trim();
      if (trimmed) return trimmed;
    }
    return "";
  };

  const getPrimarySrc = (photo: FeaturePhotoItem) =>
    getFirstValidUrl([
      photo.src,
      photo.original,
      photo.originalUrl,
      photo.thumbnail,
      photo.thumbnailUrl
    ]);
  const getThumbnailSrc = (photo: FeaturePhotoItem) =>
    getFirstValidUrl([
      photo.thumbnail,
      photo.thumbnailUrl,
      photo.src,
      photo.original,
      photo.originalUrl
    ]);
  const getAltText = (photo: FeaturePhotoItem, fallback: string) =>
    photo.alt ?? photo.md_id ?? fallback;

  return (
    <div className="flex flex-col gap-2" data-testid="feature-photos">
      <span className="text-dim-gray text-sm">{label}</span>
      <div className="relative">
        {hasMultiplePhotos ? (
          <Carousel
            setApi={setCarouselApi}
            opts={{
              loop: photos.length > 1,
              watchDrag: true,
              dragFree: false
            }}
          >
            <CarouselContent>
              {thumbnailItems.map((photo, index) => (
                <CarouselItem
                  key={`${getPrimarySrc(photo) || getThumbnailSrc(photo)}-${index}`}
                  className="pl-4"
                >
                  <button
                    type="button"
                    onClick={handleOpenViewer}
                    className="block w-full"
                    aria-label="Open image viewer"
                  >
                    <PhotoImage
                      src={getPrimarySrc(photo)}
                      alt={getAltText(photo, label)}
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </button>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        ) : (
          <button
            type="button"
            onClick={handleOpenViewer}
            className="block w-full"
            aria-label="Open image viewer"
          >
            <PhotoImage
              src={getPrimarySrc(photos[0])}
              alt={getAltText(photos[0], label)}
              className="w-full h-48 object-cover rounded-lg"
            />
          </button>
        )}
      </div>

      {hasMultiplePhotos && (
        <div className="flex flex-row flex-nowrap gap-2 overflow-auto pb-2 cursor-grab">
          {thumbnailItems.map((photo, index) => {
            const isActive = index === selectedIndex;
            return (
              <button
                type="button"
                key={`${getThumbnailSrc(photo) || getPrimarySrc(photo)}-${index}`}
                onClick={() => handleThumbnailClick(index)}
                className={twMerge(
                  "rounded-md border-2 p-[1px] !size-[52px] flex-shrink-0",
                  isActive
                    ? "border-teal-500"
                    : "border-transparent hover:border-neutral-200"
                )}
                aria-label={`Select image ${index + 1}`}
              >
                <PhotoImage
                  src={getThumbnailSrc(photo)}
                  alt={getAltText(photo, `${label} thumbnail ${index + 1}`)}
                  className="!object-contain w-full mx-auto h-full rounded-[4px]"
                />
              </button>
            );
          })}
        </div>
      )}

      <Dialog open={isViewerOpen} onOpenChange={handleViewerOpenChange}>
        <DialogTitle className="sr-only">{label}</DialogTitle>
        <DialogContent className="max-w-3xl bg-transparent border-0 shadow-none">
          {selectedPhoto && (
            <div className="max-h-[80vh] flex items-center justify-center">
              <PhotoImage
                src={getPrimarySrc(selectedPhoto)}
                alt={getAltText(selectedPhoto, label)}
                className="max-h-[80vh] w-auto h-auto object-contain"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface PhotoImageProps {
  src: string;
  alt: string;
  className?: string;
}

const PhotoImage: React.FC<PhotoImageProps> = ({ src, alt, className }) => {
  const [hasError, setHasError] = useState(false);
  const shouldShowFallback = hasError || !src;

  if (shouldShowFallback) {
    return (
      <div
        role="img"
        aria-label={alt}
        className={twMerge(
          "flex items-center justify-center bg-[#CECECE]",
          className
        )}
      >
        <BrokenLinkIcon aria-hidden="true" className="h-8 w-8" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      onError={() => setHasError(true)}
      className={twMerge("w-full h-auto", className)}
    />
  );
};

export default FeatureInfoPhotos;
